import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
} from 'react'

/**
 * Inline TITLE (block name) editing for the full-size page-editor preview.
 *
 * Parallels `EditableDescription`, but the block name is PLAIN single-line text
 * (not markdown), so this uses a simple `<input>` with Enter/blur-to-save and
 * Esc-to-cancel — no TipTap, no Save/Cancel buttons.
 *
 * The full-size `DraggableContentBlockItem` provides this context so the
 * heading renderers (TextOnlyPreview's `1_column_title_top`, the title rows in
 * `2_columns_title_top_center` / `3_columns_title_left` / `category_tiles`) can
 * display and edit the CURRENT language's name without threading props through
 * every layout.
 *
 * When NO provider is present (the thumbnail path used by ContentBlocksManager
 * and CategoryForm) `EditableTitle` renders the plain `name` it's handed —
 * byte-for-byte unchanged from rendering the heading text directly.
 */
export interface EditableTitleContextValue {
  /** Current language's name ('' if that language has no profile yet). */
  rawValue: string
  /** Whether click-to-edit is enabled (full-size page editor). */
  editable: boolean
  /** Persist a new name; resolves on success, rejects on failure. */
  onSave: (next: string) => Promise<void>
}

const EditableTitleContext = createContext<EditableTitleContextValue | null>(
  null
)

export function EditableTitleProvider({
  value,
  children,
}: {
  value: EditableTitleContextValue
  children: ReactNode
}) {
  return (
    <EditableTitleContext.Provider value={value}>
      {children}
    </EditableTitleContext.Provider>
  )
}

/**
 * Returns the inline-edit context when inside a full-size preview, else `null`.
 * Consumers use `null` to mean "render the plain name as before" (thumbnail).
 */
export function useEditableTitle(): EditableTitleContextValue | null {
  return useContext(EditableTitleContext)
}

/**
 * Renders the block's title heading and, when editable, swaps it for a
 * single-line text input on click.
 *
 * IMPORTANT: this component renders the heading element ITSELF (via the `as`
 * prop, e.g. `as="h3"`) rather than being nested inside an existing
 * `<h3>`/`<p>`. The edit affordance wraps a block-level `<div>`, which would be
 * invalid HTML nested inside a `<p>`/`<h3>`. Pass the same tag + className the
 * renderer used for the static heading so the look is identical at rest.
 *
 * Behaviour:
 *  - Display mode: shows `name` (or, when editable AND empty, a muted
 *    "Click to add title" placeholder). Click / Enter opens the editor.
 *  - Edit mode: `<input>` seeded with the current language's name. ENTER or
 *    BLUR saves; ESC cancels. A `committedRef` guard prevents the Enter→blur
 *    sequence from saving twice.
 *  - Save skips when unchanged, guards against duplicate saves, and stays in
 *    edit mode if the save fails so the user can retry.
 */
export function EditableTitle({
  as,
  name,
  className,
  inverse,
}: {
  /** Heading tag to render (e.g. 'h2', 'h3', 'p'). */
  as: ElementType
  /** Text to display in non-editing mode (the renderer's original name). */
  name: string
  /** Class names for the heading element (matches the static heading). */
  className?: string
  /** Light styling hint for dark overlays (kept for parity; unused for now). */
  inverse?: boolean
}) {
  const ctx = useEditableTitle()
  const Tag = as

  // No provider (thumbnail path) → render the plain heading exactly as before.
  if (!ctx) {
    return <Tag className={className}>{name}</Tag>
  }

  return (
    <EditableTitleInner
      ctx={ctx}
      as={as}
      className={className}
      inverse={inverse}
    />
  )
}

function EditableTitleInner({
  ctx,
  as,
  className,
  inverse,
}: {
  ctx: EditableTitleContextValue
  as: ElementType
  className?: string
  inverse?: boolean
}) {
  // When a provider is present we are language-aware: BOTH display and edit use
  // the CURRENT language's name (`rawValue`), never getBlockName's English
  // fallback. The English fallback only applies on the no-context thumbnail path
  // handled by the outer `EditableTitle`.
  const { rawValue, editable, onSave } = ctx
  const Tag = as

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(rawValue)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  // Guards the Enter→blur double-fire: Enter calls commit then blurs the input,
  // whose blur handler would commit again. Set true once a commit is in flight
  // (or has run for this edit session); reset when a new edit session opens.
  const committedRef = useRef(false)

  // Focus + select-all when edit mode opens.
  useEffect(() => {
    if (!editing) return
    const el = inputRef.current
    if (el) {
      el.focus()
      el.select()
    }
  }, [editing])

  if (!editable && !editing) {
    return <Tag className={className}>{rawValue}</Tag>
  }

  const startEditing = () => {
    if (saving) return
    committedRef.current = false
    setDraft(rawValue)
    setEditing(true)
  }

  const cancel = () => {
    committedRef.current = true
    setEditing(false)
    setDraft(rawValue)
  }

  const commit = async () => {
    if (committedRef.current) return
    committedRef.current = true
    // Single-line plain text — strip any stray newlines and trim edges.
    const next = draft.replace(/[\r\n]+/g, ' ').trim()
    if (next === rawValue) {
      setEditing(false)
      return
    }
    if (saving) return
    setSaving(true)
    try {
      await onSave(next)
      setEditing(false)
    } catch {
      // Keep the edited text and stay in edit mode so the user can retry.
      committedRef.current = false
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    const hasContent = rawValue.trim().length > 0
    return (
      <Tag
        role="textbox"
        tabIndex={0}
        onClick={startEditing}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            startEditing()
          }
        }}
        title="Click to edit title"
        className={`${className ?? ''} -m-1 cursor-text rounded p-1 transition-colors hover:bg-black/[0.03] hover:outline hover:outline-1 hover:outline-dashed ${
          inverse ? 'hover:outline-white/40' : 'hover:outline-gray-300'
        }`}
      >
        {hasContent ? (
          rawValue
        ) : (
          <span
            className={`italic font-normal ${
              inverse ? 'text-white/70' : 'text-gray-400'
            }`}
          >
            Click to add title
          </span>
        )}
      </Tag>
    )
  }

  // Edit mode: single-line input. Enter or blur saves; Esc cancels.
  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      disabled={saving}
      onChange={e => setDraft(e.target.value)}
      onKeyDown={e => {
        // Keep keystrokes (and Esc/Enter) from reaching the page editor.
        e.stopPropagation()
        if (e.key === 'Enter') {
          e.preventDefault()
          // Commit, then blur. committedRef stops the resulting blur from
          // committing a second time.
          void commit()
          inputRef.current?.blur()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          cancel()
        }
      }}
      onBlur={() => {
        void commit()
      }}
      // Clicks inside the input must not bubble to an ancestor click-to-edit
      // region (the heading itself becomes a textbox in display mode).
      onClick={e => e.stopPropagation()}
      className={`${className ?? ''} -m-1 w-full max-w-full rounded border border-gray-300 bg-white p-1 text-gray-800 shadow-sm outline-none focus:border-gray-400`}
    />
  )
}
